import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import castArray from 'lodash/castArray';

/**
 * Kept in its own module rather than in the Hooks barrel: the ORM hooks the
 * barrel re-exports also consume this hook, so defining it there put them in an
 * import cycle with it.
 */
export const useQueryStringUpdate = params => {
    const history = useHistory();
    const { hash, pathname, search } = useLocation();
    const {
        doUpdate = true,
        filters,
        limit,
        offset,
        query,
        sort,
        sortDirection,
        linkOperator,
    } = params;

    const newSearch = useMemo(() => {
        const searchParams = new URLSearchParams();
        query && searchParams.set('query', query);
        offset && searchParams.set('offset', offset);
        limit && limit !== 25 && searchParams.set('limit', limit);
        linkOperator && searchParams.set('linkOperator', linkOperator);
        sort && sortDirection !== 'none' && searchParams.set('sort', sort);
        sort && sortDirection !== 'none' && searchParams.set('sortDirection', sortDirection);
        Object.entries(filters).forEach(([key, value]) => {
            // If multiple of the same filter exist for multi-filtering then use multiple of the same query parameter key
            if (linkOperator && Array.isArray(value)) {
                for (const val of value) {
                    searchParams.append(key, val);
                }
            } else if (value && `${value}`.length) {
                searchParams.set(key, castArray(value).join(','));
            }
        });
        // Sort the keys for predictable order
        searchParams.sort();
        return searchParams.toString();
    }, [search, JSON.stringify(filters), limit, linkOperator, offset, query, sort, sortDirection]);

    const oldUrl = `${pathname}${search}${hash}`;
    const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ''}${hash}`;

    useEffect(() => {
        doUpdate && oldUrl !== newUrl && history.replace(newUrl);
    }, [doUpdate, oldUrl, newUrl]);
};

export default useQueryStringUpdate;
